import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import {
  selectIsPMP,
  selectPopularProductIds,
  selectRecommendedProductIds,
} from 'lib/util/privateMarketplace';
import { toProductShape } from 'lib/util/productShape';
import { useCurrencyFormatter } from 'hooks/useCurrencyFormatter';
import { Product } from 'models/product';
import { TagList } from 'components/TagList/TagList';
import { CurrentUrlContext } from 'contexts/CurrentUrlContext';
import { ShareButtons } from 'components/Sns/ShareButtons';
import { getThemeCss } from 'components/Themes';
import { BookingWidgetThemeContext } from 'contexts/BookingWidgetThemeContext';
import { ProductDetailsBookmarkButton } from 'components/ProductDetailsBookmarkButton/ProductDetailsBookmarkButton';

interface Props {
  product: Product;
}

export const ProductContentsHeader = ({ product: apiProduct }: Props) => {
  const { t, i18n } = useTranslation();
  const { url } = React.useContext(CurrentUrlContext);
  const bookingWidgetTheme = React.useContext(BookingWidgetThemeContext);

  const product = toProductShape(apiProduct);

  const popularProductIds = useSelector(selectPopularProductIds);
  const ranking = popularProductIds.indexOf(product.id) + 1;
  const isPopular = !!ranking;

  const recommendedProductIds = useSelector(selectRecommendedProductIds);
  const isRecommended = recommendedProductIds.includes(product.id);

  const isPMP = useSelector(selectIsPMP);

  const currencyFormatter = useCurrencyFormatter();

  let discountText = '';
  if (product.bestDiscountPercent || product.bestDiscountGross) {
    discountText = t('{{discount}} OFF', {
      discount: product.bestDiscountPercent
        ? `${product.bestDiscountPercent}%`
        : currencyFormatter.format(product.bestDiscountGross),
    });
  }

  const showReviewCount = product.reviewCount != null && product.reviewCount !== 0;

  return (
    <>
      <div className="base-single__header">
        <div
          className={clsx(
            'base-single__title',
            getThemeCss(bookingWidgetTheme, 'base-single__title')
          )}
        >
          <h1
            className={clsx(
              'base-single__title__h1',
              getThemeCss(bookingWidgetTheme, 'base-single__title__h1'),
              'base-key__text'
            )}
          >
            {product.name}
          </h1>
        </div>
        {(isPopular || isRecommended || discountText || isPMP || showReviewCount) && (
          <div
            className={clsx(
              'base-single__headerinfo',
              getThemeCss(bookingWidgetTheme, 'base-single__headerinfo')
            )}
          >
            {(isPopular || isRecommended || discountText) && (
              <div className="base-single__headerinfo__labels">
                {isPopular && (
                  <div className="base-single__headerinfo__labels__item ranking">
                    <img src="/static/images/ic_ranking_yellow.svg" alt="ranking" />
                    {t('High Ranking')}
                  </div>
                )}
                {isRecommended && (
                  <div className="base-single__headerinfo__labels__item recommend">
                    <img src="/static/images/ic_recommend_green.svg" alt="recommended" />
                    {t('Recommended')}
                  </div>
                )}
                {discountText && (
                  <div className="base-single__headerinfo__labels__item promotion">
                    <img src="/static/images/ic_special.svg" alt="special" />
                    {discountText}
                  </div>
                )}
              </div>
            )}
            <div className="base-single__headerinfo__other">
              {showReviewCount && (
                <div className="base-single__headerinfo__other__review">
                  <a href="#reviews">
                    <img src="/static/images/ic_star_on.svg" alt="star" />
                    <span>{(product.reviewRating ?? 0).toFixed(1)}</span>
                    <span>({product.reviewCount})</span>
                  </a>
                </div>
              )}
              {isPMP && <ProductDetailsBookmarkButton productId={product.id} />}
            </div>
          </div>
        )}
      </div>
      <div
        className={clsx(
          'base-single__2ndheaderinfo',
          getThemeCss(bookingWidgetTheme, 'base-single__2ndheaderinfo')
        )}
      >
        <div className="base-single__2ndheaderinfo__left">
          <TagList tags={product.tags} />
        </div>
        <div className="base-single__2ndheaderinfo__right">
          <ShareButtons url={url} productName={product?.name || ''} locale={i18n.language} />
        </div>
      </div>
    </>
  );
};
